import {computed, ref} from 'vue';
import axios from 'axios';
import {useMutation, useQuery, useQueryClient} from '@tanstack/vue-query';
import {errorToast, successToast} from '@/composables/useToasts';

const selectedUserId = ref(null);

export const useFeatureFlags = () => {
    const queryClient = useQueryClient();

    const searchUsers = (search) => {
        if (!search) {
            return Promise.resolve([]);
        }

        return queryClient.fetchQuery({
            queryKey: ['admin-feature-flag-users', search],
            queryFn: async () => {
                const { data } = await axios.get('/ajax/v2/admin/feature-flags/users', {
                    params: { search, limit: 10 },
                });

                return data.data.map((user) => ({
                    value: user.id,
                    label: `${user.name} (${user.email})`,
                }));
            },
            staleTime: 30000,
        });
    };

    const queryKey = computed(() => ['admin-feature-flags', selectedUserId.value]);

    const featureQuery = useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            const { data } = await axios.get(`/ajax/v2/admin/feature-flags/users/${selectedUserId.value}`);
            return data;
        },
        enabled: computed(() => !!selectedUserId.value),
    });

    const user = computed(() => featureQuery.data.value?.user ?? null);
    const features = computed(() => featureQuery.data.value?.features ?? []);

    const toggleMutation = useMutation({
        mutationFn: ({ feature, enabled }) =>
            axios.post(`/ajax/v2/admin/feature-flags/users/${selectedUserId.value}`, { feature, enabled }),
        onMutate: async ({ feature, enabled }) => {
            await queryClient.cancelQueries({ queryKey: queryKey.value });

            const previous = queryClient.getQueryData(queryKey.value);

            queryClient.setQueryData(queryKey.value, (currentData) => ({
                ...currentData,
                features: currentData.features.map((flag) => {
                    if (flag.feature === feature) {
                        return { ...flag, enabled };
                    }
                    return flag;
                }),
            }));

            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey.value, context.previous);
            }
            errorToast('Something went wrong');
        },
        onSuccess: () => successToast('Feature flag updated'),
        onSettled: () => queryClient.invalidateQueries({ queryKey: queryKey.value }),
    });

    const toggleFeature = (feature, enabled) => {
        toggleMutation.mutate({ feature, enabled });
    };

    return {
        ...featureQuery,
        selectedUserId,
        searchUsers,
        user,
        features,
        toggleFeature,
        isToggling: toggleMutation.isPending,
    };
};
